import CustomButton from 'components/Buttons/CustomButton'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Modal, ModalBody, Spinner } from 'reactstrap'
import { URL_DETAIL_TRANSACTION } from 'utils/pathUrl'
import { createOrder, splitInventory } from '../sellCommodityAction'
import { setProject, toggleModal } from '../sellCommoditySlice'
import ProjectCheck, { getProjectMismatch } from '../ProjectCheck'

function ModalTrx({ isManual }) {
  const { isOpenModal, orders, invPacking, inventories } = useSelector(
    (s) => s.sellCommodity,
  )
  const { profile } = useSelector((s) => s.dashboard)
  const dispatch = useDispatch()
  const [errMsg, setErrMsg] = useState('')
  const [loading, setLoading] = useState(false)
  // Project picked in the project check; null = keep orders.project
  const [pickedProject, setPickedProject] = useState(null)
  const { enqueueSnackbar } = useSnackbar()

  const { mismatched, untagged } = getProjectMismatch(
    invPacking,
    orders?.project,
  )
  const showProjectCheck =
    mismatched.length > 0 || (untagged.length > 0 && Boolean(orders?.project))

  const toggle = useCallback(() => {
    dispatch(toggleModal())
  }, [dispatch])

  const navigate = useNavigate()

  const toNumberSmart = (val) => {
    if (val === null || val === undefined) return 0
    if (typeof val === 'number') return val

    let s = String(val).trim()

    s = s.replace(/[^\d.,-]/g, '')

    const hasDot = s.includes('.')
    const hasComma = s.includes(',')

    if (hasDot && hasComma) {
      const lastDot = s.lastIndexOf('.')
      const lastComma = s.lastIndexOf(',')
      if (lastComma > lastDot) {
        s = s.replace(/\./g, '').replace(',', '.')
      } else {
        s = s.replace(/,/g, '')
      }
      return parseFloat(s) || 0
    }

    if (hasComma) {
      const parts = s.split(',')
      if (parts.length > 2) {
        const last = parts.pop()
        s = parts.join('') + '.' + last
      } else {
        s = s.replace(',', '.')
      }
      return parseFloat(s) || 0
    }

    if (hasDot) {
      const dotCount = (s.match(/\./g) || []).length
      if (dotCount > 1) {
        return parseFloat(s.replace(/\./g, '')) || 0
      }
      const [intPart, frac] = s.split('.')
      if (
        frac &&
        frac.length === 3 &&
        /^\d+$/.test(intPart) &&
        /^\d+$/.test(frac)
      ) {
        return parseFloat(intPart + frac) || 0
      }
      return parseFloat(s) || 0
    }

    return parseFloat(s) || 0
  }

  const payloadOrderModified = (orders, invPacking) => {
    const resolveSku = (row) => {
      const directSku = row?.SKU || row?.sku
      if (directSku) return directSku

      if (!row?.inventoryId || !Array.isArray(inventories)) return ''

      const matchedInv = inventories.find(
        (inv) => Number(inv?.id) === Number(row?.inventoryId),
      )

      return (
        matchedInv?.sku ||
        matchedInv?.SKU ||
        matchedInv?.item?.SKU ||
        matchedInv?.item?.sku ||
        ''
      )
    }

    const dataCommodity = orders?.item?.filter((ord) => !ord.isPacking) ?? []

    const invPackingList = Array.isArray(invPacking) ? invPacking : []
    const invByInventoryId = new Map(
      invPackingList
        .filter(
          (row) => row?.inventoryId !== null && row?.inventoryId !== undefined,
        )
        .map((row) => [String(row.inventoryId), row]),
    )
    const invByFieldNum = new Map(
      invPackingList
        .filter((row) => row?.fieldNum !== null && row?.fieldNum !== undefined)
        .map((row) => [String(row.fieldNum), row]),
    )

    const newData = dataCommodity.map((e, i) => {
      const orderItemId = e.orderItemId || e.fieldNum || i + 1
      const byInventoryId =
        e?.inventoryId !== null && e?.inventoryId !== undefined
          ? invByInventoryId.get(String(e.inventoryId))
          : null
      const byFieldNum = invByFieldNum.get(String(orderItemId))
      const overrideRow = byInventoryId || byFieldNum

      const amount = toNumberSmart(overrideRow?.amount ?? e?.amount)
      const price = toNumberSmart(overrideRow?.price ?? e?.price)
      const totalPrice =
        overrideRow?.totalPrice !== null &&
        overrideRow?.totalPrice !== undefined
          ? toNumberSmart(overrideRow.totalPrice)
          : amount * price

      return {
        ...e,
        ...(overrideRow || {}),
        orderItemId,
        SKU: resolveSku(overrideRow || e),
        price,
        amount,
        totalPrice,
      }
    })

    const existingInventoryIds = new Set(
      newData
        .map((item) => item?.inventoryId)
        .filter((id) => id !== null && id !== undefined)
        .map((id) => String(id)),
    )

    const existingFieldNums = new Set(
      newData
        .map((item) => item?.orderItemId)
        .filter((id) => id !== null && id !== undefined)
        .map((id) => String(id)),
    )

    const filteredInvData = (invPacking ?? []).filter((e) => {
      const inventoryId = e?.inventoryId
      const fieldNum = e?.fieldNum

      const hasSameInventory =
        inventoryId !== null &&
        inventoryId !== undefined &&
        existingInventoryIds.has(String(inventoryId))

      // For manage-stock backfilled cards, fieldNum mirrors orderItemId.
      const hasSameFieldNum =
        fieldNum !== null &&
        fieldNum !== undefined &&
        existingFieldNums.has(String(fieldNum))

      return !hasSameInventory && !hasSameFieldNum
    })

    const newInvData = filteredInvData.map((e, i) => ({
      orderItemId: newData.length + i + 1,
      SKU: resolveSku(e),
      ...e,
      price: toNumberSmart(e?.price),
      amount: toNumberSmart(e?.amount),
      totalPrice: toNumberSmart(e?.totalPrice),
    }))

    const seenInventoryIds = new Set()

    const dedupedItems = [...newData, ...newInvData]
      .filter((item) => {
        const hasInventory =
          item?.inventoryId !== null && item?.inventoryId !== undefined
        if (!hasInventory) return true

        const inventoryId = String(item.inventoryId)
        if (!inventoryId) return true

        if (seenInventoryIds.has(inventoryId)) {
          return false
        }

        seenInventoryIds.add(inventoryId)
        return true
      })
      .map((item, idx) => ({
        ...item,
        orderItemId: idx + 1,
      }))

    const normalizedOrder = {
      type: orders?.order?.type || orders?.type || 'SELL',
      notes: orders?.order?.notes ?? '',
      transaction_number: orders?.order?.transaction_number ?? '',
      files: orders?.order?.files ?? [],
      ...(orders?.order || {}),
    }

    const picId =
      orders?.seller?.otherPicUserId ||
      orders?.buyer?.otherPicUserId ||
      orders?.seller?.otherPicUser?.userId ||
      orders?.buyer?.otherPicUser?.userId

    const profileUserId = profile?.userId || profile?.ownerData?.userId || null
    const profileName =
      profile?.fullName || profile?.name || profile?.ownerData?.name || ''
    const profilePhone =
      profile?.phoneNumber || profile?.ownerData?.phoneNumber || ''
    const profileEmail = profile?.email || profile?.ownerData?.email || ''

    const normalizedSeller = {
      fullName: profileName,
      phoneNumber: profilePhone,
      email: profileEmail,
      userId: profileUserId,
      ...(orders?.seller || {}),
    }

    if (!normalizedSeller?.userId && profileUserId) {
      normalizedSeller.userId = profileUserId
    }

    const payload = {
      ...orders,
      userId: orders?.userId || normalizedSeller?.userId || profileUserId,
      seller: normalizedSeller,
      order: normalizedOrder,
      item: dedupedItems,
    }

    if (picId) {
      payload.otherPicUserId = picId
    }

    if (Object.prototype.hasOwnProperty.call(payload, 'type')) {
      delete payload.type
    }

    if (isManual) {
      payload.order = {
        ...payload.order,
        unconfirmed: true,
        proofOfPayment: '',
      }
    }
    return payload
  }

  const handleSavedContacts = (e) => {
    const saved =
      JSON.parse(localStorage.getItem(`savedContacts-${profile?.userId}`)) ?? []
    let newListComodities = [...saved]

    const updatedOptions = newListComodities.filter(
      (item) => item.userId !== e.userId,
    )

    updatedOptions.unshift(e)

    if (updatedOptions.length > 5) {
      const removedItem = updatedOptions.pop()

      newListComodities = {
        removedItem,
      }
    }

    localStorage.setItem(
      `savedContacts-${profile?.userId}`,
      JSON.stringify(updatedOptions),
    )
  }

  const handleSubmit = async (projectOverride) => {
    setLoading(true)
    try {
      const cleanedOrders = {
        ...orders,
        ...(projectOverride !== undefined && { project: projectOverride }),
      }
      delete cleanedOrders.step
      delete cleanedOrders.isLoading

      const validatedOrders = {
        ...cleanedOrders,
        item: cleanedOrders.item.map((orderItem) => ({
          ...orderItem,
          totalPrice: orderItem.totalPrice === '' ? 0 : orderItem.totalPrice,
        })),
      }

      validatedOrders.item.forEach((item, idx) => {
        if (item?.inventoryId) {
          // If originalAmount is missing, try to get it from inventories
          let originalAmt = item?.originalAmount
          if (!originalAmt && inventories?.length > 0) {
            const inv = inventories.find((i) => i.id === item.inventoryId)
            if (inv) {
              originalAmt = inv.amount
            }
          }
        }
      })
      const splitCandidates = (validatedOrders.item || []).filter((oi) => {
        const hasInventory = !!oi?.inventoryId
        const isPacking = !!oi?.isPacking
        const amountNum = toNumberSmart(oi?.amount)

        // Try multiple sources for originalAmount
        let originalAmountNum = 0

        // 1. Try originalAmount field (set by handleChange when selecting inventory)
        if (oi?.originalAmount) {
          originalAmountNum = toNumberSmart(oi.originalAmount)
        }

        // 2. Try inventories array from Redux (current inventory data)
        if (
          originalAmountNum === 0 &&
          hasInventory &&
          inventories?.length > 0
        ) {
          // 2a. Try matching by ID first
          const invById = inventories.find((i) => i.id === oi.inventoryId)
          if (invById?.amount) {
            originalAmountNum = toNumberSmart(invById.amount)
          }

          // 2b. If ID match failed or not found, try matching by nameItem or itemId
          if (originalAmountNum === 0) {
            const matchedInvs = inventories.filter((inv) => {
              // Match by name
              const nameMatch =
                (inv.nameItem || '').toLowerCase() ===
                (oi.nameItem || '').toLowerCase()
              // Match by itemId if available
              const itemIdMatch = oi.itemId && inv.itemId === oi.itemId

              return nameMatch || itemIdMatch
            })

            if (matchedInvs.length > 0) {
              // Sort by amount descending to find the largest potential parent inventory
              matchedInvs.sort(
                (a, b) => toNumberSmart(b.amount) - toNumberSmart(a.amount),
              )
              const bestMatch = matchedInvs[0]
              const bestAmount = toNumberSmart(bestMatch.amount)

              // Only accept if it looks like a parent inventory (larger amount)
              if (bestAmount > amountNum) {
                originalAmountNum = bestAmount

                // CRITICAL FIX: Update the inventoryId to the found match!
                // This ensures the backend splits the CORRECT inventory (44522)
                // instead of the old/empty one (20312)
                oi.inventoryId = bestMatch.id
                oi.originalAmount = originalAmountNum
              }
            }
          }
        }

        // 3. Try to parse from nameItem display text (e.g., "50.990 kg | GUDANG Bima bro")
        if (originalAmountNum === 0 && oi?.nameItem) {
          const nameText = oi.nameItem
          // Look for pattern like "50.990 kg" or "50,990 kg" at the start
          const match = nameText.match(/^([\d.,]+)\s*(kg|Kg|KG)/i)
          if (match) {
            const parsedAmount = toNumberSmart(match[1])
            if (parsedAmount > amountNum) {
              // Only use if larger than order amount
              originalAmountNum = parsedAmount
            }
          }
        }

        // 4. Try itemDetail fields (from repeat order transaction data)
        if (originalAmountNum === 0 && oi?.itemDetail) {
          const itemDetail = oi.itemDetail
          // Try various possible fields
          const possibleFields = [
            itemDetail.amountOriginal,
            itemDetail.originalAmount,
            itemDetail.inventoryAmount,
            itemDetail.item?.amount,
            itemDetail.newAmount, // sometimes used in transactions
          ]

          for (const field of possibleFields) {
            if (field) {
              const parsed = toNumberSmart(field)
              if (parsed > amountNum) {
                // Only use if it's larger than current amount
                originalAmountNum = parsed
                break
              }
            }
          }
        }

        // Simply check: if amount < originalAmount, need to split
        const needsSplit =
          amountNum > 0 &&
          originalAmountNum > 0 &&
          amountNum < originalAmountNum
        return hasInventory && !isPacking && needsSplit
      })

      const splitCandidateInventoryIds = new Set(
        splitCandidates
          .map((oi) => oi?.inventoryId)
          .filter((id) => id !== null && id !== undefined)
          .map((id) => String(id)),
      )

      // Handle split for edited invPacking items (grouped and non-grouped).
      let updatedInvPacking = [...(invPacking || [])]
      const invPackingSplitCandidates = (invPacking || []).filter(
        (packItem) => {
          const hasInventory = !!packItem?.inventoryId
          const isEdited = !!packItem?.isEdited
          const amountNum = toNumberSmart(packItem?.amount)
          const originalAmountNum = toNumberSmart(packItem?.originalAmount)
          const alreadyPlannedInOrderSplit =
            hasInventory &&
            splitCandidateInventoryIds.has(String(packItem?.inventoryId))

          // Check if amount was changed and is less than original
          const needsSplit =
            amountNum > 0 &&
            originalAmountNum > 0 &&
            amountNum < originalAmountNum

          return (
            hasInventory &&
            isEdited &&
            needsSplit &&
            !alreadyPlannedInOrderSplit
          )
        },
      )

      if (invPackingSplitCandidates.length > 0) {
        const invPackingSplitPromises = invPackingSplitCandidates.map(
          (packItem) => {
            const splitAmount = toNumberSmart(packItem?.amount)
            const originalAmount = toNumberSmart(packItem?.originalAmount)
            const remainder = originalAmount - splitAmount
            const unitValue = packItem?.unit?.value || packItem?.unit || 'Kg'

            // Urutan: remainder dulu, baru splitAmount
            // Sehingga inventory yang dijual ada di index 1 response
            const arrNew = []

            if (remainder > 0) {
              arrNew.push({
                amount: remainder,
                unit: unitValue,
                itemId: packItem?.itemId,
              })
            }

            arrNew.push({
              amount: splitAmount,
              unit: unitValue,
              itemId: packItem?.itemId,
            })

            const payload = {
              inventoryId: packItem?.inventoryId,
              arrNew,
              notes: `auto-split-group-item-${packItem?.inventoryId}-${Date.now()}`,
            }
            return dispatch(splitInventory(payload))
              .unwrap()
              .then((result) => ({
                packItem,
                result,
              }))
          },
        )

        const invPackingSplitResults = await Promise.all(
          invPackingSplitPromises,
        )

        // Update invPacking with new inventory IDs from split results
        updatedInvPacking = updatedInvPacking.map((packItem) => {
          if (!packItem?.inventoryId || !packItem?.isEdited) {
            return packItem
          }

          const splitResult = invPackingSplitResults.find(
            (r) => r.packItem?.inventoryId === packItem?.inventoryId,
          )

          if (!splitResult) {
            return packItem
          }

          try {
            // Response dari splitInventory langsung berupa array, bukan nested
            const inventories = Array.isArray(splitResult.result)
              ? splitResult.result
              : splitResult.result?.items ||
                splitResult.result?.data ||
                splitResult.result?.newInventories ||
                splitResult.result?.result ||
                []

            // Ambil inventory ID dari index 1 (item yang dijual)
            // karena arrNew urutan: [remainder, splitAmount]
            // response urutan: [remainder inventory, splitAmount inventory]
            const matched =
              Array.isArray(inventories) && inventories.length > 1
                ? inventories[1] // Index 1 = item yang dijual
                : inventories[0] // Fallback ke index 0 jika hanya 1 item

            if (matched?.id) {
              return {
                ...packItem,
                inventoryId: matched.id,
                isEdited: false,
              }
            }
          } catch (error) {
            // Error matching split result
          }

          return { ...packItem, isEdited: false }
        })
      }

      let updatedOrdersAfterSplit = { ...validatedOrders }
      if (splitCandidates.length > 0) {
        const splitPromises = splitCandidates.map((oi) => {
          const splitAmount = toNumberSmart(oi?.amount)
          const originalAmount = toNumberSmart(oi?.originalAmount)
          const remainder = originalAmount - splitAmount
          const unitValue = oi?.unit?.value || oi?.unit || 'Kg'

          const arrNew = [
            {
              amount: splitAmount,
              unit: unitValue,
              itemId: oi?.itemId,
            },
          ]

          if (remainder > 0) {
            arrNew.push({
              amount: remainder,
              unit: unitValue,
              itemId: oi?.itemId,
            })
          }

          const payload = {
            inventoryId: oi?.inventoryId,
            arrNew,
            notes: `auto-split-for-order-${oi?.inventoryId}-${Date.now()}`,
          }
          return dispatch(splitInventory(payload)).unwrap()
        })

        const splitResults = await Promise.all(splitPromises)

        updatedOrdersAfterSplit = {
          ...validatedOrders,
          item: validatedOrders.item.map((oi) => {
            if (
              !oi?.inventoryId ||
              oi?.isPacking !== false ||
              oi?.isEdited !== true
            ) {
              return oi
            }

            const result = splitResults.find((r) => {
              const inventories =
                r?.items || r?.data || r?.newInventories || r?.result || []
              if (Array.isArray(inventories) && inventories.length > 0)
                return true
              return false
            })

            try {
              const inventories =
                result?.items ||
                result?.data ||
                result?.newInventories ||
                result?.result ||
                []
              const requestedAmount = toNumberSmart(oi?.amount)
              const matched = Array.isArray(inventories)
                ? inventories.find((x) => {
                    const amt = toNumberSmart(x?.amount)
                    return amt === requestedAmount
                  }) || inventories[0]
                : null

              if (matched?.id) {
                return {
                  ...oi,
                  inventoryId: matched.id,
                  isEdited: false,
                }
              }
            } catch {
              // Error matching inventory
            }
            return { ...oi, isEdited: false }
          }),
        }
      }

      transactionPartners()
      const res = await dispatch(
        createOrder(
          payloadOrderModified(updatedOrdersAfterSplit, updatedInvPacking),
        ),
      )
      if (res?.error) {
        throw res
      } else {
        const transactionId = res?.payload?.escrowpaymentId || res?.payload?.id
        if (!transactionId) {
          setErrMsg('Transaksi berhasil dibuat, tetapi ID transaksi tidak ditemukan.')
          return
        }
        handleSavedContacts(orders?.buyer)
        localStorage.removeItem(`userDataSell-${profile?.userId}`)
        toggle()
        enqueueSnackbar('Transaksi berhasil dibuat', { variant: 'success' })
        navigate(`${URL_DETAIL_TRANSACTION}/${transactionId}?type=SELL`, {
          state: res?.payload?.escrowpaymentId
            ? { escrowpaymentId: transactionId }
            : { tradeshortcutId: transactionId },
        })
      }
    } catch (error) {
      const message =
        error?.payload ||
        error?.message ||
        error?.error?.message ||
        'Terjadi kesalahan saat memproses transaksi.'
      setErrMsg(message)
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const transactionPartners = () => {
    const sellerId = profile?.userId
    const buyerId = orders?.buyer?.userId
    const cash = orders?.buyer?.allowPayment?.cash === 1 ? 1 : 0
    const payable = orders?.buyer?.allowPayment?.payable === 1 ? 1 : 0

    const newEntry = { sellerId, buyerId, cash, payable }

    const existing = JSON.parse(
      localStorage.getItem('allowPaymentHistory') || '[]',
    )
    const updatedList = existing.some(
      (item) => item.sellerId === sellerId && item.buyerId === buyerId,
    )
      ? existing.map((item) =>
          item.sellerId === sellerId && item.buyerId === buyerId
            ? newEntry
            : item,
        )
      : [...existing, newEntry]
    localStorage.setItem('allowPaymentHistory', JSON.stringify(updatedList))
  }

  useEffect(() => {
    if (!isOpenModal) {
      setErrMsg('')
    }
  }, [isOpenModal])

  return (
    <Modal
      isOpen={isOpenModal}
      toggle={toggle}
      onClosed={() => setPickedProject(null)}
      centered
      style={{ maxWidth: showProjectCheck ? '460px' : '400px' }}
      data-testid="sell-form-modal-info"
    >
      <ModalBody>
        <div className="text-left">
          <h5 className="fw-bold text-md">Buat Transaksi</h5>
          <p className="text-sm">
            Pastikan data yang kamu masukkan sudah benar.
          </p>
        </div>

        {showProjectCheck && (
          <div className="mb-3">
            <ProjectCheck
              project={orders?.project}
              mismatched={mismatched}
              untagged={untagged}
              value={pickedProject}
              onChange={setPickedProject}
            />
          </div>
        )}

        {errMsg ? (
          <div className="my-3">
            <small className="text-danger">
              <AlertCircle size={15} /> {errMsg}
            </small>
          </div>
        ) : (
          <></>
        )}

        <div className="border-0 flex-centered flex-row gap-2 w-100">
          <CustomButton
            color="secondary"
            onClick={() => {
              toggle()
            }}
            outline
            className="w-50"
            testId="sell-form-modal-info-close"
          >
            <span className="text-normal">Batal</span>
          </CustomButton>
          <CustomButton
            disabled={loading}
            color="primary"
            onClick={() => {
              if (pickedProject) {
                const project = pickedProject.value
                  ? {
                      id: pickedProject.value,
                      value: pickedProject.value,
                      label: pickedProject.label,
                    }
                  : null
                dispatch(setProject(project))
                handleSubmit(project)
              } else {
                handleSubmit()
              }
            }}
            className="w-50"
            testId="sell-form-modal-info-submit"
          >
            {loading ? (
              <Spinner size="sm" color="light" />
            ) : (
              <span className="text-normal">Lanjutkan</span>
            )}
          </CustomButton>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ModalTrx
